import { TypedDocumentNode, gql } from '@apollo/client';
import { faPlus } from '@fortawesome/pro-solid-svg-icons';
import classnames from 'classnames';
import { ReactNode, createRef, useCallback, useMemo, useState } from 'react';

import { Rarity, Sport } from '@sorare/core/src/__generated__/globalTypes';
import IconButton from '@sorare/core/src/atoms/buttons/IconButton';
import useDecks from '@sorare/core/src/hooks/decks/useDecks';
import useScreenSize from '@sorare/core/src/hooks/device/useScreenSize';
import { StackProps } from '@sorare/core/src/lib/algolia';

import CommonCardPreview from 'components/card/CommonCardPreview';
import { AddTokenToDeck } from 'components/decks/AddTokenToDeck';
import DeckEditorDialog from 'components/decks/DeckEditorDialog';
import Empty from 'components/market/Empty';
import {
  AnimatedGrid,
  AnimatedGridItem,
  Grid,
  GridItem,
  GridOverlayLoadingIndicator,
} from 'components/market/Grid';
import {
  PlayerDetails,
  PlayerDrawer,
  Props as PlayerDrawerProps,
} from 'components/player/PlayerDrawer';
import { Token } from 'components/token/Token';
import { useMarketplaceContext } from 'contexts/Marketplace';
import { CardResultsProps } from 'searchCards/AdvancedCardSearch/types';
import Pagination from 'searchCards/Pagination';

import { CardPreviewGrid_card } from './__generated__/index.graphql';

type Item = CardPreviewGrid_card & { stack?: StackProps };

type Props = Pick<
  CardResultsProps,
  | 'hideOwner'
  | 'galleryOwnerSlug'
  | 'topic'
  | 'hideSorareUser'
  | 'showDesktopFilter'
  | 'editableLists'
  | 'onlyPrimary'
> & {
  items: Item[] | undefined;
  loading: boolean;
  PlayerDetailsComponent: PlayerDetails;
  sport: Sport;
};

const TokenItem = ({
  item,
  displayMarketplaceOnboardingTooltip,
  galleryOwnerSlug,
  hideOwner,
  hideSorareUser,
  setCardOpened,
  hideDetails,
  action,
  onlyPrimary,
}: {
  item: Item;
  displayMarketplaceOnboardingTooltip?: boolean;
  galleryOwnerSlug?: string;
  hideOwner?: boolean;
  hideSorareUser?: boolean;
  setCardOpened: (cardSlug: string, playerSlug: string) => void;
  hideDetails: boolean;
  action?: ReactNode;
  onlyPrimary: boolean;
}) => {
  const onInfoClick = useCallback(() => {
    setCardOpened(item.slug, item.anyPlayer.slug);
  }, [item, setCardOpened]);

  if (item.rarityTyped !== Rarity.common) {
    return (
      <Token
        card={item}
        galleryOwnerSlug={galleryOwnerSlug}
        displayMarketplaceOnboardingTooltip={
          displayMarketplaceOnboardingTooltip
        }
        hideOwner={hideOwner}
        hideSorareUser={hideSorareUser}
        stack={item.stack}
        onInfoClick={onInfoClick}
        hideDetails={hideDetails}
        action={action}
        onlyPrimary={onlyPrimary}
      />
    );
  }
  return (
    <CommonCardPreview card={item} hideDetails={hideDetails} action={action} />
  );
};

const getItemKey = (item: Item) => {
  return item.assetId!;
};

export const CardPreviewGrid = ({
  items,
  hideOwner,
  galleryOwnerSlug,
  topic,
  hideSorareUser,
  loading,
  showDesktopFilter,
  editableLists,
  onlyPrimary = false,
  PlayerDetailsComponent,
  sport,
}: Props) => {
  const { decks } = useDecks({ sport: Sport.FOOTBALL });
  const { hideDetails } = useMarketplaceContext();
  const [createListDialogOpen, setCreateListDialogOpen] = useState(false);
  const [addCardToDeck, setAddCardToDeck] =
    useState<CardPreviewGrid_card | null>(null);
  const [cardOpened, setCardOpened] =
    useState<PlayerDrawerProps['cardOpened']>(null);

  const { up: isTabletOrAbove } = useScreenSize('tablet');
  const ref = useMemo(() => createRef<HTMLDivElement>(), []);

  if (items?.length === 0 && !loading) {
    return <Empty isGallery={!!galleryOwnerSlug} topic={topic} />;
  }

  const flipKey = `${items?.map(item => getItemKey(item)).join('-')}${
    showDesktopFilter ? '-showDesktopFilter' : ''
  }`;

  const GridComponent = isTabletOrAbove ? AnimatedGrid : Grid;
  const GridItemComponent = isTabletOrAbove ? AnimatedGridItem : GridItem;

  return (
    <div ref={ref}>
      <GridComponent
        staggerConfig={{
          default: {
            speed: 0.4,
          },
        }}
        flipKey={flipKey}
        className={classnames({ hideDetails, showDesktopFilter })}
      >
        {items?.map((item, i) => {
          return (
            <GridItemComponent key={getItemKey(item)} flipId={getItemKey(item)}>
              <TokenItem
                item={item}
                displayMarketplaceOnboardingTooltip={i === 0}
                galleryOwnerSlug={galleryOwnerSlug}
                hideOwner={hideOwner}
                hideSorareUser={hideSorareUser}
                setCardOpened={(cardSlug, playerSlug) =>
                  setCardOpened({
                    cardSlug,
                    playerSlug,
                  })
                }
                hideDetails={!!(hideDetails && galleryOwnerSlug)}
                onlyPrimary={onlyPrimary}
                action={
                  editableLists && item.rarityTyped !== Rarity.common ? (
                    <IconButton
                      onClick={() => {
                        if (!decks.length) {
                          setCreateListDialogOpen(true);
                        }
                        setAddCardToDeck(item);
                      }}
                      color="tertiary"
                      icon={faPlus}
                      small
                    />
                  ) : undefined
                }
              />
            </GridItemComponent>
          );
        })}
      </GridComponent>
      <Pagination rootRef={ref} visible={!!items?.length} />
      {loading && <GridOverlayLoadingIndicator />}
      {addCardToDeck &&
        !createListDialogOpen &&
        addCardToDeck.rarityTyped !== Rarity.common && (
          <AddTokenToDeck
            card={addCardToDeck}
            onClose={() => setAddCardToDeck(null)}
            addList={() => setCreateListDialogOpen(true)}
          />
        )}
      {createListDialogOpen && (
        <DeckEditorDialog
          sport={sport}
          onClose={() => {
            setAddCardToDeck(addCardToDeck);
            setCreateListDialogOpen(false);
          }}
        />
      )}
      <PlayerDrawer
        cardOpened={cardOpened}
        onClose={() => {
          setCardOpened(null);
        }}
        PlayerDetails={PlayerDetailsComponent}
      />
    </div>
  );
};

CardPreviewGrid.fragments = {
  card: gql`
    fragment CardPreviewGrid_card on AnyCardInterface {
      slug
      anyPlayer {
        slug
      }
      ...AddTokenToDeck_anyCard @include(if: $editableLists)
      ...Token_anyCard
      ...CommonCardPreview_card
    }
    ${Token.fragments.anyCard}
    ${CommonCardPreview.fragments.card}
    ${AddTokenToDeck.fragments.anyCard}
  ` as TypedDocumentNode<CardPreviewGrid_card>,
};

export default CardPreviewGrid;
